// Case Detail Page
// BP-10-04: Attorney Frontend

import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit2,
  MoreVertical,
  Calendar,
  FileText,
  DollarSign,
  Heart,
  Calculator,
  Users,
  Share2,
  Archive,
  Trash2,
} from 'lucide-react';
import { useCase, useArchiveCase } from '@/features/attorney/hooks/useAttorney';
import { CaseStatusBadge, CaseQuickStats } from '../components';
import type { CaseStatus } from '@/features/attorney/types/attorney';

const tabs = [
  { id: 'overview', label: 'Overview', icon: FileText },
  { id: 'timeline', label: 'Timeline', icon: Calendar },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'expenses', label: 'Expenses', icon: DollarSign },
  { id: 'life-care', label: 'Life Care Plan', icon: Heart },
  { id: 'settlement', label: 'Settlement', icon: Calculator },
];

export function CaseDetailPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [showMenu, setShowMenu] = useState(false);

  const { data: caseData, isLoading } = useCase(caseId!);
  const archiveMutation = useArchiveCase();

  const handleArchive = async () => {
    if (!caseId) return;
    if (confirm('Are you sure you want to archive this case?')) {
      await archiveMutation.mutateAsync(caseId);
      navigate('/attorney/cases');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4" />
        <div className="h-64 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="text-center py-16">
        <h2 className="text-xl font-semibold text-navy mb-2">Case not found</h2>
        <Link to="/attorney/cases" className="text-sage hover:text-sage-dark">
          Back to cases
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Link
            to="/attorney/cases"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors mt-1"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-semibold text-navy">
                {caseData.family?.name || 'Unknown Family'}
              </h1>
              <CaseStatusBadge status={caseData.status as CaseStatus} />
            </div>
            <p className="text-gray-600">
              {caseData.caseNumber || caseData.internalReference}
              {caseData.defendant && (
                <span className="text-gray-400"> • vs {caseData.defendant}</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={`/attorney/cases/${caseId}/edit`}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Edit2 className="w-4 h-4" />
            Edit
          </Link>

          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <MoreVertical className="w-5 h-5 text-gray-600" />
            </button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      // Handle share
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Share2 className="w-4 h-4" />
                    Share with Client
                  </button>
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      handleArchive();
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Archive className="w-4 h-4" />
                    Archive Case
                  </button>
                  <hr className="my-1" />
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      // Handle delete
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Case
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <CaseQuickStats case={caseData} />

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-sage text-sage'
                  : 'border-transparent text-gray-600 hover:text-navy hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Case Summary */}
            <div>
              <h3 className="text-lg font-semibold text-navy mb-3">
                Case Summary
              </h3>
              <p className="text-gray-600 whitespace-pre-wrap">
                {caseData.caseSummary || 'No summary provided.'}
              </p>
            </div>

            {/* Case Details Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-medium text-navy">Case Information</h4>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Case Type</dt>
                    <dd className="font-medium">{caseData.caseType || 'N/A'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Court</dt>
                    <dd className="font-medium">{caseData.court || 'N/A'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Jurisdiction</dt>
                    <dd className="font-medium">{caseData.jurisdiction || 'N/A'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Judge</dt>
                    <dd className="font-medium">{caseData.judge || 'N/A'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Statute of Limitations</dt>
                    <dd className="font-medium">
                      {caseData.statuteOfLimitations
                        ? new Date(caseData.statuteOfLimitations).toLocaleDateString()
                        : 'N/A'}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-navy">Defendant Information</h4>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Defendant</dt>
                    <dd className="font-medium">{caseData.defendant || 'N/A'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Defense Counsel</dt>
                    <dd className="font-medium">
                      {caseData.defendantCounsel || 'N/A'}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Liability Assessment */}
            {caseData.liabilityAssessment && (
              <div>
                <h4 className="font-medium text-navy mb-2">
                  Liability Assessment
                </h4>
                <p className="text-gray-600 whitespace-pre-wrap">
                  {caseData.liabilityAssessment}
                </p>
              </div>
            )}

            {/* Client Portal Status */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium text-navy">Client Portal</p>
                  <p className="text-sm text-gray-500">
                    {caseData.clientPortalEnabled
                      ? 'Enabled - Client can view case updates'
                      : 'Disabled - Client cannot access portal'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  // Toggle client portal
                }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  caseData.clientPortalEnabled
                    ? 'bg-sage/10 text-sage'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {caseData.clientPortalEnabled ? 'Enabled' : 'Enable'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="text-center py-8 text-gray-500">
            <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>View timeline at</p>
            <Link
              to={`/attorney/cases/${caseId}/timeline`}
              className="text-sage hover:text-sage-dark"
            >
              Case Timeline Page
            </Link>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>View documents at</p>
            <Link
              to={`/attorney/cases/${caseId}/documents`}
              className="text-sage hover:text-sage-dark"
            >
              Case Documents Page
            </Link>
          </div>
        )}

        {activeTab === 'expenses' && (
          <div className="text-center py-8 text-gray-500">
            <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>View expense report at</p>
            <Link
              to={`/attorney/cases/${caseId}/expenses`}
              className="text-sage hover:text-sage-dark"
            >
              Case Expenses Page
            </Link>
          </div>
        )}

        {activeTab === 'life-care' && (
          <div className="text-center py-8 text-gray-500">
            <Heart className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>View life care plans at</p>
            <Link
              to={`/attorney/cases/${caseId}/life-care`}
              className="text-sage hover:text-sage-dark"
            >
              Life Care Plan Page
            </Link>
          </div>
        )}

        {activeTab === 'settlement' && (
          <div className="text-center py-8 text-gray-500">
            <Calculator className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>View settlement tools at</p>
            <Link
              to={`/attorney/cases/${caseId}/settlement`}
              className="text-sage hover:text-sage-dark"
            >
              Settlement Planner Page
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default CaseDetailPage;


